import { useMemo, useState } from "react";
import { number, object } from "prop-types";
import { observer } from "mobx-react";
import { WorkTypeValueState, TaskTypes } from "@repo/utils";
import { Dialog } from "@components";
import { IconButton } from "@material-ui/core";
import { Percent, Settings } from "@assets";
import { ResetValueButton, useEstimateStructure } from "@tools";
import { CellFormatSelect } from "./components/CellFormatSelect";
import { CellSettingsTitle } from "./components/CellSettingsTitle";
import { CellAutomation } from "./components/CellAutomation";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

export const TaskFixedPriceCellSettings = observer(({
  element,
                                                      tableDepth,
}) => {
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  const { useMinMax } = structure.settings;
  
  const [ visible, show ] = useState(false);
  
  const {
    calculatedFixedPrice,
    isFixedPriceOverridden,
    hasFixedPriceAutomation,
    hasOwnFixedPriceAutomation,
  } = element;

  const lighterColor = useMemo(() => (
    tableDepth === 0 ? "color-primary-lighter" : undefined
  ), [tableDepth]);
  
  const forcedState = calculatedFixedPrice?.forcedState || (useMinMax ? WorkTypeValueState.MIN_MAX : WorkTypeValueState.SINGLE);
  
  const handlePopup = () => show(!visible);
  
  const handleVariantChange = (e, v) => {
    element.setFixedPriceForcedState(v);
  }
  
  const handleAutomationChange = ({ percent, usedSections }) => {
    element.setFixedPriceAutomation(percent, usedSections);
  }
  
  return <>
    {
      hasFixedPriceAutomation && !isFixedPriceOverridden &&
      <IconButton
        onClick={ hasOwnFixedPriceAutomation ? handlePopup : undefined }
        color={ hasOwnFixedPriceAutomation ? "primary" : "secondary" }
        size="small"
        className={classnames("noDrag p-0-5 text-sm", !hasOwnFixedPriceAutomation && "expand", lighterColor )}
        tabIndex={-1}
      >
        <Percent />
      </IconButton>
    }
    {
      isFixedPriceOverridden &&
      <ResetValueButton action={element.resetFixedPriceAutomation}>
        {t("views.editor.revert_value")}
      </ResetValueButton>
    }
    <IconButton
      onClick={handlePopup}
      color="primary"
      size="small"
      className={classnames("noDrag p-0-5 text-sm cellHoverIcon nonDisplay", lighterColor) }
      tabIndex={-1}
    >
      <Settings />
    </IconButton>
    {
      visible &&
      <Dialog
        open={visible}
        onClose={handlePopup}
        width={520}
        title={<CellSettingsTitle element={element} />}
      >
        <CellFormatSelect
          value={forcedState}
          disabled={element.type !== TaskTypes.FIXED_PRICE}
          onChange={handleVariantChange}
        />
        <CellAutomation
          section={element.section}
          element={element}
          onSave={handleAutomationChange}
        />
      </Dialog>
    }
  </>
})

TaskFixedPriceCellSettings.propTypes = {
  element: object.isRequired,
  tableDepth: number.isRequired,
}
